import { useRef, useCallback } from 'react';
import { Button, HOT_KEY, Typography, useHotKey } from '@web-chat-core/ui-kit';
import ModalOverlay from '@web-chat-core/ui-kit/core/ModalOverlay';
import { useHotKeyIsolate } from '@web-chat-core/ui-kit/core/HotKeys';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { selectIsModalOpened, selectModalDataData } from 'state/selectors';
import { ModalName } from 'features/Modals/ModalProvider';
import { closeModal } from 'useCase/modal/useModal';
import { Timer } from './Timer/Timer';
import {
    TextWrapper,
    TimerWrapper,
    StyledModalBody,
    StyledModalHeader,
    StyledModalContent,
    StyledModalFooter,
} from './view';
import { BEFORE_CALL_ENDIND_TIME } from './consts';

type ContinueCallConfirmModalDataType = {
    confirmCallback: () => void;
    declineCallback: () => void;
};

export const ContinueCallConfirmModal = observer(() => {
    const { t } = useTranslation();

    const isOpen = selectIsModalOpened(ModalName.CONTINUE_CALL_CONFIRM);

    const modalData = selectModalDataData() as ContinueCallConfirmModalDataType;
    const { confirmCallback, declineCallback } = modalData;

    const endCallButtonRef = useRef<HTMLButtonElement>(null);

    const onConfirm = useCallback(() => {
        confirmCallback();
        closeModal();
    }, []);

    const onClose = useCallback(() => {
        declineCallback();
        closeModal();
    }, []);

    const onHotKeyConfirm = useCallback(() => {
        const isFocused = endCallButtonRef.current != null && document.activeElement === endCallButtonRef.current;

        if (isFocused) {
            onClose();
        } else {
            onConfirm();
        }
    }, [onConfirm]);

    useHotKeyIsolate({ enabled: () => isOpen });

    useHotKey(
        HOT_KEY.ESC,
        onConfirm,
        {
            enabled: () => isOpen,
        },
        [isOpen],
    );

    useHotKey(
        HOT_KEY.ENTER,
        onHotKeyConfirm,
        {
            enabled: () => isOpen,
        },
        [isOpen],
    );

    if (!isOpen) return null;

    return (
        <ModalOverlay name="continue-call-confirm" isNeedOverlay={true}>
            <StyledModalBody width="400px">
                <StyledModalHeader title={null} showCloseButton={true} onClose={onConfirm} />
                <StyledModalContent>
                    <TimerWrapper>
                        <Timer time={BEFORE_CALL_ENDIND_TIME} onTimeOut={onClose} />
                    </TimerWrapper>
                    <TextWrapper>
                        <Typography variant="title9" textAlign="center">
                            {t('vks_continue_call_title')}
                        </Typography>
                        <Typography variant="subtitle3" colorVariant="second" textAlign="center" margin="16px 0 0 0">
                            {t('vks_continue_call_message')}
                        </Typography>
                    </TextWrapper>
                </StyledModalContent>
                <StyledModalFooter>
                    <Button ref={endCallButtonRef} variant="secondaryWithoutBg" withoutBg={true} onClick={onClose}>
                        {t('vks_continue_call_end')}
                    </Button>
                    <Button onClick={onConfirm}>{t('vks_continue_call_button')}</Button>
                </StyledModalFooter>
            </StyledModalBody>
        </ModalOverlay>
    );
});
